import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import api from "../lib/api";
import { JOB_POSITIONS } from "../lib/data";
import { Reveal, Eyebrow } from "../components/Reveal";
import { useTitle } from "../hooks/useTitle";

export default function Careers() {
  useTitle("Careers");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", location: "", position: "",
    experience: "", education: "", linkedin: "", cover_note: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("resume", file);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted! We will review and get back to you.");
      setForm({ full_name: "", mobile: "", email: "", location: "", position: "", experience: "", education: "", linkedin: "", cover_note: "" });
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-[#0A192F] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F5132]/40 focus:border-[#0F5132] transition";

  return (
    <div data-testid="careers-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow light>Careers</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-[1.1]">
            Work with SterlingNest
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-xl leading-relaxed">
            Join a team that believes accounting is taught best by doing. We hire trainers, counsellors and
            operations talent who care about practical education.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 space-y-5 shadow-sm" data-testid="careers-form">
            <h2 className="font-display font-extrabold text-xl text-[#0A192F]">Apply Now</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Full Name *</label>
                <input data-testid="careers-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Mobile *</label>
                <input data-testid="careers-mobile-input" className={inputCls} value={form.mobile} onChange={set("mobile")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Email</label>
                <input data-testid="careers-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Location</label>
                <input data-testid="careers-location-input" className={inputCls} value={form.location} onChange={set("location")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Position Applied For</label>
                <select data-testid="careers-position-select" className={inputCls} value={form.position} onChange={set("position")}>
                  <option value="">Select position</option>
                  {JOB_POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Experience</label>
                <input data-testid="careers-experience-input" className={inputCls} placeholder="e.g. 3 years" value={form.experience} onChange={set("experience")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Education</label>
                <input data-testid="careers-education-input" className={inputCls} placeholder="e.g. B.Com, M.Com, CA Inter" value={form.education} onChange={set("education")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#334E68] mb-1.5">LinkedIn URL</label>
                <input data-testid="careers-linkedin-input" className={inputCls} placeholder="https://linkedin.com/in/..." value={form.linkedin} onChange={set("linkedin")} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Cover Note</label>
              <textarea data-testid="careers-cover-input" rows={4} className={inputCls} placeholder="Tell us why you'd be a great fit..." value={form.cover_note} onChange={set("cover_note")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Resume (PDF, max 5MB)</label>
              <label
                data-testid="careers-resume-upload"
                className="flex items-center justify-center gap-3 w-full py-8 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0F5132] cursor-pointer transition-colors bg-[#F8FAFC]"
              >
                <UploadCloud size={20} className="text-[#0F5132]" />
                <span className="text-sm font-semibold text-[#334E68]">
                  {file ? file.name : "Click to upload your resume"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="careers-resume-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="careers-submit-btn"
              className="w-full py-3.5 rounded-lg bg-[#0A192F] text-white font-display font-bold text-sm hover:bg-[#0F5132] transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
